import {
  ChangeEventHandler,
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';

import { Trans } from '@lingui/react/macro';
import { CSVLocationsDownloader, CSVReader } from '../ProviderCsv';
import { getSubdomain } from '@duke-hq/lib';
import { ProviderLocation, ProviderWithContactDetails } from '../ProviderTypes';
import { CheckIcon, Trash2 } from 'lucide-react';
import locationsData from '@duke-hq/svc-api/provider/data/locationsData';
import { testIds } from './testIds';
import { useFlag } from '@duke-hq/flags/react';
import { ProviderEmailDomains } from './ProviderEmailDomains';

interface ProviderDetails {
  // email_domain is only collected when creating a provider; in edit mode the
  // provider entity no longer carries it (domains live in ProviderEmailDomain).
  provider: Omit<ProviderWithContactDetails, 'email_domain'> & { email_domain?: string };
  providerId?: number;
  withContact?: boolean;
  withLocations?: boolean;
  disabled?: boolean;
  onSubmit: (provider: any) => void;
}

interface LocationsInterface {
  headers: string[];
  rows: string[][];
  errors: string[];
}

interface Location {
  SITE: string;
  REGION: string;
  STATE: string;
}

interface InvalidRowInterface {
  index: number;
  error: string[][]; // each error is a row, which is an array of strings
  label: { name: string; type: 'state' | 'site' };
}

const sampleCsv: Location[] = [
  {
    SITE: 'Coomera',
    REGION: 'Queensland Region',
    STATE: 'Queensland',
  },
  {
    SITE: 'Bendigo',
    REGION: 'Victoria West',
    STATE: 'Victoria',
  },
  {
    SITE: 'Ballina',
    REGION: 'NSW Region',
    STATE: 'New South Wales',
  },
];

export function ProviderForm({
  provider,
  providerId,
  withContact,
  withLocations,
  disabled,
  onSubmit,
}: ProviderDetails): React.ReactElement {
  const [formData, setFormData] = useState<ProviderWithContactDetails>({
    ...provider,
    email_domain: provider.email_domain ?? '',
  });
  const [locations, setLocations] = useState<LocationsInterface>();
  const [invalidRows, setInvalidRows] = useState<InvalidRowInterface[]>([]);
  const [stateIndex, setStateIndex] = useState<number>();
  const [siteIndex, setSiteIndex] = useState<number>();
  const [isCsvMalformed, setIsCsvMalformed] = useState<boolean>(false);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean | undefined>(disabled);
  const INCENTIVE_PROGRAM_RELEASE = useFlag('INCENTIVE_PROGRAM_RELEASE');

  const validStates = useMemo(() => locationsData.map((location) => location.State), []);

  const toggleIsBillable = useCallback(async () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      is_billable: !prevFormData.is_billable,
    }));
  }, [setFormData]);

  const toggleIsSiteLocation = useCallback(async () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      uses_new_referral_process: !prevFormData.uses_new_referral_process,
    }));
  }, [setFormData]);

  const toggleIsExternalIdFieldMandatory = useCallback(async () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      is_external_id_field_mandatory: !prevFormData.is_external_id_field_mandatory,
    }));
  }, [setFormData]);

  const toggleAzureSsoOidc = useCallback(async () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      azure_sso_oidc: !prevFormData.azure_sso_oidc,
    }));
  }, [setFormData]);

  const toggleIncentiveProgramEnabled = useCallback(async () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      incentiveProgramEnabled: !prevFormData.incentiveProgramEnabled,
    }));
  }, [setFormData]);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const {
        target: { name, value },
      } = e;

      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    },
    [setFormData],
  );

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleCsvData = useCallback(
    (results: LocationsInterface) => {
      if (results && results.errors.length) {
        setIsCsvMalformed(true);
        return;
      } else {
        setIsCsvMalformed(false);
      }

      setLocations(results);

      const stateIdx = results.headers.indexOf('STATE');
      const siteIdx = results.headers.indexOf('SITE');
      setStateIndex(stateIdx);
      setSiteIndex(siteIdx);

      const rowsWithErrors: InvalidRowInterface[] = [];

      results.rows.forEach((row: string[], index) => {
        const errors: string[][] = [];
        let label: { name: string; type: 'state' | 'site' } | null = null;

        if (stateIdx !== -1 && typeof row[stateIdx] === 'string' && isStateInvalid(row[stateIdx])) {
          errors.push(row);
          label = { name: row[stateIdx], type: 'state' };
        }

        if (
          stateIdx !== -1 &&
          siteIdx !== -1 &&
          typeof row[stateIdx] === 'string' &&
          typeof row[siteIdx] === 'string' &&
          isSiteInvalid(row[stateIdx], row[siteIdx])
        ) {
          errors.push(row);
          label = { name: row[siteIdx], type: 'site' };
        }

        if (errors.length > 0 && label) {
          rowsWithErrors.push({ index, error: errors, label });
        }
      });

      setInvalidRows(rowsWithErrors);

      if (rowsWithErrors.length === 0) {
        setFormData((prev) => ({
          ...prev,
          locations: csvToJson(results),
        }));
      }
    },
    [setIsCsvMalformed, setLocations, setStateIndex, setSiteIndex, setInvalidRows, setFormData],
  );

  const isStateInvalid = useCallback(
    (state: string) => !validStates.includes(state),
    [validStates],
  );

  const isSiteInvalid = useCallback(
    (state: string, site: string, omit: boolean = true) => {
      if (omit) {
        return false;
      }
      const stateIndex = validStates.findIndex((validState) => validState === state);
      if (stateIndex !== -1) {
        return !locationsData.some(
          (location) =>
            location.State === state && location.Site.toLowerCase() === site.toLowerCase(),
        );
      }
      return false;
    },
    [validStates],
  );

  const isRowInvalid = useCallback(
    (index: number) => {
      return invalidRows.some((invalidRow) => invalidRow.index === index);
    },
    [invalidRows],
  );

  const handleRemoveCsv = useCallback(() => {
    setIsCsvMalformed(false);
    setInvalidRows([]);
    setLocations({ headers: [], rows: [], errors: [] });
    setFormData((prevFormData) => {
      const { locations, ...updatedFormData } = prevFormData;
      return updatedFormData;
    });
  }, [setIsCsvMalformed, setInvalidRows, setLocations, setFormData]);

  const handleRemoveLocation = useCallback(
    (index: number) => {
      const updatedLocations = locations?.rows.filter((_, i) => i !== index);
      const location = {
        headers: locations?.headers ?? [],
        rows: updatedLocations ?? [],
        errors: [],
      };

      setLocations(location);
      setFormData((prevFormData) => ({
        ...prevFormData,
        locations: csvToJson(location),
      }));

      const updatedInvalidRows = invalidRows
        .filter((invalidRow) => invalidRow.index !== index)
        .map((invalidRow) => ({
          ...invalidRow,
          index: invalidRow.index > index ? invalidRow.index - 1 : invalidRow.index,
        }));

      setInvalidRows(updatedInvalidRows);
    },
    [locations, invalidRows, setLocations, setFormData, setInvalidRows],
  );

  useEffect(() => {
    setIsButtonLoading(disabled);
  }, [disabled]);

  return (
    <Box as="form" onSubmit={handleSubmit} data-testid={testIds.form}>
      <VStack spacing={4} align="stretch">
        <HStack spacing={3}>
          <FormControl isRequired>
            <FormLabel>
              <Trans>Provider name</Trans>
            </FormLabel>
            <Input
              autoFocus
              autoComplete="off"
              type="text"
              name="name"
              value={formData.name ?? ''}
              onChange={handleChange}
            />
            <FormHelperText>
              <Trans>The full name of the provider</Trans>
            </FormHelperText>
          </FormControl>
        </HStack>

        <HStack spacing={3}>
          <FormControl isRequired>
            <FormLabel>
              <Trans>Site prefix</Trans>
            </FormLabel>
            <InputGroup>
              <Input
                autoComplete="off"
                type="text"
                name="subdomain"
                placeholder="provider"
                value={formData.subdomain ?? ''}
                onChange={handleChange}
              />
              <InputRightAddon>
                <span>{`.${getSubdomain(provider.subdomain)}`}</span>
              </InputRightAddon>
            </InputGroup>
            <FormHelperText>
              <Trans>The prefix to use for the provider domain</Trans>
            </FormHelperText>
          </FormControl>
        </HStack>

        {providerId != null ? (
          <ProviderEmailDomains providerId={providerId} />
        ) : (
          <HStack spacing={3}>
            <FormControl isRequired>
              <FormLabel>
                <Trans>Default email domain</Trans>
              </FormLabel>
              <InputGroup>
                <InputLeftAddon>
                  <span>@</span>
                </InputLeftAddon>
                <Input
                  autoComplete="off"
                  type="text"
                  name="email_domain"
                  value={formData.email_domain ?? ''}
                  onChange={handleChange}
                />
              </InputGroup>
              <FormHelperText>
                <Trans>User email addresses are restricted to this domain.</Trans>
              </FormHelperText>
            </FormControl>
          </HStack>
        )}

        <HStack spacing={3}>
          <FormControl>
            <FormLabel htmlFor="is_billable">
              <Trans>Billable</Trans>
            </FormLabel>
            <Switch
              id="is_billable"
              name="is_billable"
              isChecked={formData.is_billable}
              colorScheme="brand"
              size="md"
              onChange={toggleIsBillable}
              data-testid={testIds.toggleIsBillable}
            />
            <FormHelperText>
              <Trans>Include this provider when generating invoices</Trans>
            </FormHelperText>
          </FormControl>
        </HStack>

        <HStack spacing={3}>
          <FormControl>
            <FormLabel htmlFor="uses_new_referral_process">
              <Trans>Uses new referral process</Trans>
            </FormLabel>
            <Switch
              id="uses_new_referral_process"
              name="uses_new_referral_process"
              isChecked={formData.uses_new_referral_process}
              colorScheme="brand"
              size="md"
              onChange={toggleIsSiteLocation}
              data-testid={testIds.toggleUseNewReferralProcess}
            />
          </FormControl>
        </HStack>

        <HStack spacing={3}>
          <FormControl>
            <FormLabel htmlFor="is_external_id_field_mandatory">
              <Trans>
                Purchase Order (or unique identifier) is required when creating a referral
              </Trans>
            </FormLabel>
            <Switch
              id="is_external_id_field_mandatory"
              name="is_external_id_field_mandatory"
              isChecked={formData.is_external_id_field_mandatory}
              colorScheme="brand"
              size="md"
              onChange={toggleIsExternalIdFieldMandatory}
              data-testid={testIds.toggleIsExternalIdFieldMandatory}
            />
          </FormControl>
        </HStack>

        <HStack spacing={3}>
          <FormControl>
            <FormLabel htmlFor="azure_sso_oidc">
              <Trans>Microsoft SSO</Trans>
            </FormLabel>
            <Switch
              id="azure_sso_oidc"
              name="azure_sso_oidc"
              isChecked={formData.azure_sso_oidc}
              colorScheme="brand"
              size="md"
              onChange={toggleAzureSsoOidc}
              data-testid={testIds.toggleAzureSsoOidc}
            />
            <FormHelperText>
              <Trans>Enable single sign-on via Microsoft</Trans>
            </FormHelperText>
          </FormControl>
        </HStack>

        {INCENTIVE_PROGRAM_RELEASE ? (
          <HStack spacing={3}>
            <FormControl>
              <FormLabel htmlFor="a">
                <Trans>Incentive program</Trans>
              </FormLabel>
              <Switch
                id="incentiveProgramEnabled"
                name="incentiveProgramEnabled"
                isChecked={formData.incentiveProgramEnabled}
                colorScheme="brand"
                size="md"
                onChange={toggleIncentiveProgramEnabled}
                data-testid={testIds.toggleIncentiveProgramEnabled}
              />
              <FormHelperText>
                <Trans>Enable incentive program</Trans>
              </FormHelperText>
            </FormControl>
          </HStack>
        ) : null}

        {withContact ? (
          <>
            <Heading as="h6" fontSize="lg" mb={0}>
              <Trans>Provider contact details</Trans>
            </Heading>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Email</Trans>
              </FormLabel>
              <Input
                autoComplete="off"
                placeholder="Email"
                name="contactEmail"
                type="email"
                value={formData.contactEmail ?? ''}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                <Trans>First name</Trans>
              </FormLabel>
              <Input
                autoComplete="off"
                placeholder="First name"
                name="contactFirstName"
                type="text"
                value={formData.contactFirstName ?? ''}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Last name</Trans>
              </FormLabel>
              <Input
                autoComplete="off"
                placeholder="Last name"
                name="contactLastName"
                type="text"
                value={formData.contactLastName ?? ''}
                onChange={handleChange}
              />
            </FormControl>
          </>
        ) : null}

        {withLocations ? (
          <>
            <Heading as="h6" fontSize="lg" mb={0}>
              Provider locations
            </Heading>

            <Flex direction={'column'}>
              <Text fontSize="sm">Import locations from a CSV file here.</Text>
              <Text fontSize="sm">
                <b>Please note:</b> Only CSV files are accepted.
              </Text>
            </Flex>

            <Flex direction={'column'} align={'flex-end'}>
              <Text fontSize="sm">
                Download a sample CSV file{' '}
                <CSVLocationsDownloader
                  type="link"
                  filename={'provider-locations-sample'}
                  data={sampleCsv}
                  data-testid={testIds.downloadSampleCsv}
                >
                  <Text
                    as="span"
                    color="brand.500"
                    fontWeight="bold"
                    _hover={{ textDecoration: 'underline', color: 'brand.700' }}
                  >
                    here
                  </Text>
                </CSVLocationsDownloader>
              </Text>
            </Flex>

            <CSVReader onRead={handleCsvData} onFileRemove={handleRemoveCsv} />

            {locations ? (
              <>
                <CsvImportErrorAlert invalidRows={invalidRows} />
                <LocationsTable
                  locations={locations}
                  isRowInvalid={isRowInvalid}
                  handleRemoveLocation={handleRemoveLocation}
                  stateIndex={stateIndex}
                  siteIndex={siteIndex}
                  isStateInvalid={isStateInvalid}
                  isSiteInvalid={isSiteInvalid}
                />
              </>
            ) : null}
          </>
        ) : null}

        <Flex justify={'flex-end'}>
          <Button
            right={0}
            type="submit"
            isLoading={isButtonLoading}
            loadingText="Submitting"
            isDisabled={invalidRows.length > 0 || isCsvMalformed}
          >
            {formData.id ? <Trans>Update provider</Trans> : <Trans>Create provider</Trans>}
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}

function CsvImportErrorAlert({ invalidRows }: { invalidRows: InvalidRowInterface[] }) {
  if (invalidRows.length === 0) {
    return null;
  }

  return (
    <Box my={1}>
      <Alert status="error" flexDirection="column" alignItems="flex-start" borderRadius="lg">
        <HStack mb={2}>
          <AlertIcon />
          <AlertTitle>CSV Import Errors!</AlertTitle>
        </HStack>
        <AlertDescription>
          <Text fontSize="sm" mb={2}>
            The following rows contain errors and need to be corrected:
          </Text>
          <Box as="ul">
            {invalidRows.map((invalidRow: InvalidRowInterface, idx: number) => (
              <Box as="li" key={idx} fontSize="sm">
                <Text as="span">Row {invalidRow.index + 1}:</Text>
                <Text as="span" fontWeight="bold">
                  {invalidRow.label.type === 'state' ? ' State ' : ' Site '}
                </Text>
                <Text as="span" color="danger" fontWeight="bold">
                  {invalidRow.label.name}
                </Text>
              </Box>
            ))}
          </Box>
        </AlertDescription>
      </Alert>
    </Box>
  );
}

function LocationsTable({
  locations,
  isRowInvalid,
  handleRemoveLocation,
  stateIndex,
  siteIndex,
  isStateInvalid,
  isSiteInvalid,
}) {
  return (
    <Box maxHeight="200px" overflowY="auto">
      <Table>
        <Thead>
          <Tr>
            <Th p={2}></Th>
            {locations.headers.length > 0 &&
              locations.headers.map((header: string, i: number) => (
                <Th key={i} p={2}>
                  <Text fontWeight="bold">{header}</Text>
                </Th>
              ))}
            <Th p={2}></Th>
          </Tr>
        </Thead>
        <Tbody>
          {locations.rows.map((row: string[], rowIndex: number) => {
            const isInvalidRow = isRowInvalid(rowIndex);
            return (
              <Tr key={rowIndex} className={isInvalidRow ? 'danger' : ''}>
                <Td p={2}>
                  <Text fontWeight="bold">{rowIndex + 1}</Text>
                </Td>
                {row.map((cell: string, cellIndex: number) => {
                  const isStateError = cellIndex === stateIndex && isStateInvalid(row[stateIndex]);
                  const isSiteError =
                    cellIndex === siteIndex && isSiteInvalid(row[stateIndex], row[siteIndex]);

                  return (
                    <Td
                      key={cellIndex}
                      color={isStateError || isSiteError ? 'danger' : ''}
                      fontWeight={isStateError || isSiteError ? 'bold' : 'normal'}
                      bg={isStateError || isSiteError ? 'danger.muted' : ''}
                      p={2}
                    >
                      <Text>{cell}</Text>
                    </Td>
                  );
                })}
                <Td p={2}>
                  {isInvalidRow ? (
                    <HStack alignItems="center" justifyContent="center">
                      <Box onClick={() => handleRemoveLocation(rowIndex)} cursor="pointer">
                        <Icon as={Trash2} color="danger" fontSize="lg" />
                      </Box>
                    </HStack>
                  ) : (
                    <HStack alignItems="center" justifyContent="center">
                      <Box
                        as={CheckIcon}
                        size="20px"
                        borderRadius="full"
                        bg="success"
                        color="white"
                        padding={1}
                      />
                    </HStack>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function csvToJson(data: LocationsInterface): ProviderLocation[] {
  const { headers, rows } = data;
  const result: ProviderLocation[] = [];

  rows.forEach((row: any) => {
    const location: ProviderLocation = { SITE: '', REGION: '', STATE: '' };
    headers.forEach((header: string, index: number) => {
      location[header] = row[index];
    });

    if (Object.keys(location).length > 0) {
      result.push(location);
    }
  });

  return result;
}
